//frontend/src/components/Dashboard/UserDashboard.jsx

import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Alert, ProgressBar, Spinner } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
    FaFileAlt, 
    FaClock, 
    FaCheckCircle, 
    FaPlus,
    FaSpinner,
    FaExclamationTriangle,
    FaChartLine,
    FaCalendar,
    FaBell,
    FaEye
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../services/api';

const UserDashboard = () => {
    const { user } = useAuth();
    const [dossiers, setDossiers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [profileCompletion, setProfileCompletion] = useState(0);
    const [notifications, setNotifications] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            // Charger les dossiers
            const dossiersResponse = await api.get('/dossiers/');
            console.log('Dossiers response:', dossiersResponse.data);
            
            // Gérer les différents formats de réponse
            let dossiersData = [];
            if (dossiersResponse.data.results) {
                // Format paginé
                dossiersData = Array.isArray(dossiersResponse.data.results) 
                    ? dossiersResponse.data.results 
                    : [];
            } else if (Array.isArray(dossiersResponse.data)) {
                // Format direct
                dossiersData = dossiersResponse.data;
            }
            
            console.log('Dossiers processed:', dossiersData);
            setDossiers(dossiersData);

            // Charger le profil utilisateur
            const userResponse = await api.get('/user');
            setProfileCompletion(userResponse.data.user.profile_completion_percentage || 0);

            // Charger les notifications non lues
            const notifResponse = await api.get('/notifications/');
            const notifData = notifResponse.data.notifications.data || [];
            const unreadNotifs = notifData.filter(n => !n.is_read).slice(0, 3);
            setNotifications(unreadNotifs);
        } catch (error) {
            console.error('Error loading dashboard data:', error);
            toast.error('Erreur lors du chargement');
            setDossiers([]);
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const variants = {
            draft: 'secondary',
            submitted: 'info',
            in_review: 'warning',
            approved: 'success',
            rejected: 'danger',
        };
        
        const labels = {
            draft: 'Brouillon',
            submitted: 'Soumis',
            in_review: 'En révision',
            approved: 'Approuvé',
            rejected: 'Rejeté',
        };

        return <Badge bg={variants[status] || 'secondary'}>{labels[status] || status}</Badge>;
    };

    const getVisaTypeLabel = (type) => {
        const types = {
            work: 'Visa de Travail',
            study: 'Visa d\'Études',
            tourist: 'Visa Touristique',
            family: 'Regroupement Familial',
            business: 'Visa d\'Affaires',
        };
        return types[type] || type;
    };

    const getVisaTypeIcon = (type) => {
        const icons = {
            work: '💼',
            study: '🎓',
            tourist: '✈️',
            family: '👨‍👩‍👧‍👦',
            business: '🏢',
        };
        return icons[type] || '📄';
    };

    // Calculer les statistiques
    const stats = {
        total: dossiers.length,
        draft: dossiers.filter(d => d.status === 'draft').length,
        submitted: dossiers.filter(d => d.status === 'submitted').length,
        in_review: dossiers.filter(d => d.status === 'in_review').length,
        approved: dossiers.filter(d => d.status === 'approved').length,
        rejected: dossiers.filter(d => d.status === 'rejected').length,
    };

    const recentDossiers = [...dossiers]
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 3);

    if (loading) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Chargement de votre tableau de bord...</p>
            </Container>
        );
    }

    return (
        <Container className="py-4">
            {/* En-tête avec nom d'utilisateur */}
            <Row className="mb-4">
                <Col>
                    <h1 className="mb-2">
                        👋 Bienvenue, {user?.first_name} {user?.last_name}
                    </h1>
                    <p className="text-muted">
                        Voici un aperçu de vos demandes de visa
                    </p>
                </Col>
                <Col xs="auto">
                    <Button 
                        variant="primary" 
                        size="lg"
                        onClick={() => navigate('/dossier/create')}
                        disabled={profileCompletion < 80}
                    >
                        <FaPlus className="me-2" />
                        Nouvelle demande
                    </Button>
                </Col>
            </Row>

            {/* Alerte pour profil incomplet */}
            {profileCompletion < 80 && (
                <Alert variant="warning" className="mb-4">
                    <Row className="align-items-center">
                        <Col md={8}>
                            <FaExclamationTriangle className="me-2" />
                            <strong>Profil incomplet ({profileCompletion}%)</strong>
                            <p className="mb-0 mt-2">
                                Complétez votre profil à au moins 80% pour pouvoir créer une demande de visa
                            </p>
                        </Col>
                        <Col md={4} className="text-end">
                            <Button 
                                variant="warning" 
                                onClick={() => navigate('/profile/complete')}
                            >
                                Compléter mon profil
                            </Button>
                        </Col>
                    </Row>
                </Alert>
            )}

            {/* Cartes statistiques */}
            <Row className="mb-4 g-3">
                <Col md={6} lg={3}>
                    <Card className="stat-card h-100 border-0 shadow-sm">
                        <Card.Body className="d-flex align-items-center">
                            <div className="stat-icon-wrapper bg-primary bg-opacity-10 rounded-circle p-3 me-3">
                                <FaFileAlt size={30} className="text-primary" />
                            </div>
                            <div>
                                <h3 className="mb-0 fw-bold">{stats.total}</h3>
                                <small className="text-muted">Total dossiers</small>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={6} lg={3}>
                    <Card className="stat-card h-100 border-0 shadow-sm">
                        <Card.Body className="d-flex align-items-center">
                            <div className="stat-icon-wrapper bg-warning bg-opacity-10 rounded-circle p-3 me-3">
                                <FaClock size={30} className="text-warning" />
                            </div>
                            <div>
                                <h3 className="mb-0 fw-bold">{stats.submitted + stats.in_review}</h3>
                                <small className="text-muted">En cours</small>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={6} lg={3}>
                    <Card className="stat-card h-100 border-0 shadow-sm">
                        <Card.Body className="d-flex align-items-center">
                            <div className="stat-icon-wrapper bg-success bg-opacity-10 rounded-circle p-3 me-3">
                                <FaCheckCircle size={30} className="text-success" />
                            </div>
                            <div>
                                <h3 className="mb-0 fw-bold">{stats.approved}</h3>
                                <small className="text-muted">Approuvés</small>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={6} lg={3}>
                    <Card className="stat-card h-100 border-0 shadow-sm">
                        <Card.Body className="d-flex align-items-center">
                            <div className="stat-icon-wrapper bg-secondary bg-opacity-10 rounded-circle p-3 me-3">
                                <FaSpinner size={30} className="text-secondary" />
                            </div>
                            <div>
                                <h3 className="mb-0 fw-bold">{stats.draft}</h3>
                                <small className="text-muted">Brouillons</small>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row>
                {/* Dossiers récents */}
                <Col lg={8} className="mb-4">
                    <Card className="h-100 border-0 shadow-sm">
                        <Card.Header className="bg-white border-bottom">
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">
                                    <FaChartLine className="me-2" />
                                    Mes dossiers récents
                                </h5>
                                {dossiers.length > 0 && (
                                    <Button 
                                        variant="link" 
                                        size="sm"
                                        onClick={() => navigate('/mes-dossiers')}
                                    >
                                        Voir tout ({dossiers.length}) →
                                    </Button>
                                )}
                            </div>
                        </Card.Header>
                        <Card.Body>
                            {recentDossiers.length === 0 ? (
                                <div className="text-center py-5">
                                    <FaFileAlt size={50} className="text-muted mb-3" />
                                    <h5>Aucun dossier</h5>
                                    <p className="text-muted mb-3">
                                        Vous n'avez pas encore créé de demande de visa
                                    </p>
                                    <Button 
                                        variant="primary"
                                        onClick={() => navigate('/dossier/create')}
                                        disabled={profileCompletion < 80}
                                    >
                                        <FaPlus className="me-2" />
                                        Créer ma première demande
                                    </Button>
                                    {profileCompletion < 80 && (
                                        <p className="text-muted mt-2 small">
                                            Complétez d'abord votre profil à 80%
                                        </p>
                                    )}
                                </div>
                            ) : (
                                <div className="dossiers-list">
                                    {recentDossiers.map((dossier) => (
                                        <div 
                                            key={dossier.id} 
                                            className="dossier-item p-3 mb-3 border rounded hover-shadow"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => navigate(`/dossier/${dossier.id}`)}
                                        >
                                            <Row className="align-items-center">
                                                <Col xs="auto">
                                                    <div className="dossier-icon fs-2">
                                                        {getVisaTypeIcon(dossier.visa_type)}
                                                    </div>
                                                </Col>
                                                <Col>
                                                    <div className="d-flex justify-content-between align-items-start mb-1">
                                                        <div>
                                                            <h6 className="mb-1">
                                                                Dossier #{dossier.id}
                                                            </h6>
                                                            <small className="text-muted">
                                                                {getVisaTypeLabel(dossier.visa_type)}
                                                            </small>
                                                        </div>
                                                        {getStatusBadge(dossier.status)}
                                                    </div>
                                                    <div className="d-flex gap-3 mt-2">
                                                        <small className="text-muted">
                                                            <FaCalendar className="me-1" />
                                                            {new Date(dossier.created_at).toLocaleDateString('fr-FR')}
                                                        </small>
                                                        {dossier.documents?.length > 0 && (
                                                            <small className="text-muted">
                                                                📎 {dossier.documents.length} document(s)
                                                            </small>
                                                        )}
                                                    </div>
                                                </Col>
                                                <Col xs="auto">
                                                    <Button 
                                                        variant="outline-primary" 
                                                        size="sm"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            navigate(`/dossier/${dossier.id}`);
                                                        }}
                                                    >
                                                        <FaEye className="me-1" />
                                                        Voir
                                                    </Button>
                                                </Col>
                                            </Row>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                {/* Panneau latéral */}
                <Col lg={4}>
                    {/* Profil */}
                    <Card className="mb-4 border-0 shadow-sm">
                        <Card.Header className="bg-white border-bottom">
                            <h6 className="mb-0">
                                👤 Complétion du profil
                            </h6>
                        </Card.Header>
                        <Card.Body>
                            <div className="text-center mb-3">
                                <h2 className="mb-0">{profileCompletion}%</h2>
                                <small className="text-muted">
                                    {profileCompletion >= 80 ? '✅ Profil complet' : '⚠️ Complétez votre profil'}
                                </small>
                            </div>
                            <ProgressBar 
                                now={profileCompletion} 
                                variant={profileCompletion >= 80 ? 'success' : 'warning'}
                                style={{ height: '10px' }}
                            />
                            {profileCompletion < 100 && (
                                <div className="text-center mt-3">
                                    <Button 
                                        variant="outline-primary" 
                                        size="sm"
                                        onClick={() => navigate('/profile/complete')}
                                    >
                                        Compléter mon profil
                                    </Button>
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Notifications */}
                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-white border-bottom">
                            <div className="d-flex justify-content-between align-items-center">
                                <h6 className="mb-0">
                                    <FaBell className="me-2" />
                                    Notifications
                                </h6>
                                <Button 
                                    variant="link" 
                                    size="sm"
                                    onClick={() => navigate('/notifications')}
                                >
                                    Voir tout →
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            {notifications.length === 0 ? (
                                <div className="text-center py-3">
                                    <FaBell size={30} className="text-muted mb-2" />
                                    <p className="text-muted mb-0 small">
                                        Aucune nouvelle notification
                                    </p>
                                </div>
                            ) : (
                                <div className="notifications-list">
                                    {notifications.map((notif) => (
                                        <div 
                                            key={notif.id}
                                            className="notification-item p-2 mb-2 border-start border-3 border-primary bg-light rounded"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => navigate('/notifications')}
                                        >
                                            <small className="fw-bold d-block">{notif.title}</small>
                                            <small className="text-muted">
                                                {notif.message.substring(0, 50)}
                                                {notif.message.length > 50 && '...'}
                                            </small>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Actions rapides */}
            <Row className="mt-4">
                <Col>
                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-white border-bottom">
                            <h5 className="mb-0">⚡ Actions rapides</h5>
                        </Card.Header>
                        <Card.Body>
                            <Row className="g-3">
                                <Col md={3}>
                                    <div 
                                        className={`quick-action-card p-3 text-center border rounded hover-shadow ${profileCompletion < 80 ? 'opacity-50' : ''}`}
                                        style={{ cursor: profileCompletion >= 80 ? 'pointer' : 'not-allowed' }}
                                        onClick={() => profileCompletion >= 80 && navigate('/dossier/create')}
                                    >
                                        <FaPlus size={30} className="text-primary mb-2" />
                                        <p className="mb-0 small fw-bold">Nouvelle demande</p>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div 
                                        className="quick-action-card p-3 text-center border rounded hover-shadow"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => navigate('/mes-dossiers')}
                                    >
                                        <FaFileAlt size={30} className="text-info mb-2" />
                                        <p className="mb-0 small fw-bold">Mes dossiers</p>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div 
                                        className="quick-action-card p-3 text-center border rounded hover-shadow"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => navigate('/chat')}
                                    >
                                        💬
                                        <p className="mb-0 small fw-bold mt-2">Assistance</p>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div 
                                        className="quick-action-card p-3 text-center border rounded hover-shadow"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => navigate('/profile')}
                                    >
                                        👤
                                        <p className="mb-0 small fw-bold mt-2">Mon profil</p>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default UserDashboard;